import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import type { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../../environments/environment";

interface FeatureFlagResponse {
    key: string;
    enabled: boolean;
    firmId?: string;
    userId?: string;
}

export const FeatureFlags = {
    ADOBE_PDF_EMBED: "adobe-pdf-embed",
    ADOBE_SIGN_ALLOW_EMBEDDED_FIRM_USER_SIGNING:
        "adobe-sign-allow-embedded-firm-user-signing",
    NEW_SIGNATURE_REQUESTS_UI: "new-signature-requests-ui",
    BILLING_DASHBOARD_PROJECT_STATUS_FILTER:
        "billing-dashboard-project-status-filter",
    BILLING_DASHBOARD_CLIENT_TAGS_FILTER:
        "billing-dashboard-client-tags-filter",
    APP_NOTIFICATIONS_FULLTEXT_SEARCH: "app-notifications-fulltext-search",
    CLIENT_BILLING_NOTES: "client-billing-notes",
    INVOICE_EXPORT_HARDENING: "invoice-export-hardening",
} as const;

export const PublicFeatureFlags = {
    OAUTH_LOGIN: "oauth-login",
} as const;

@Injectable({ providedIn: "root" })
export class FeatureFlagService {
    private readonly _http = inject(HttpClient);

    private readonly _resourceUrl: string =
        environment.apiUrl + "api/feature-flags";

    /**
     * Check if a specific feature flag is enabled globally
     * @param flagKey the key of the feature flag
     * @returns Observable of the feature flag status
     */
    isFeatureEnabled(flagKey: string): Observable<boolean> {
        return this._http
            .get<FeatureFlagResponse>(`${this._resourceUrl}/${flagKey}`)
            .pipe(map((res) => this.mapResponse(res)));
    }

    /**
     * Check if a specific public feature flag is enabled globally
     * @param flagKey the key of the feature flag
     * @returns Observable of the feature flag status
     */
    isPublicFeatureEnabled(flagKey: string): Observable<boolean> {
        return this._http
            .get<FeatureFlagResponse>(`${this._resourceUrl}/public/${flagKey}`)
            .pipe(map((res) => this.mapResponse(res)));
    }

    private mapResponse(response: FeatureFlagResponse): boolean {
        return response.enabled ?? false;
    }
}
